// src/views/admin/AdminRoles.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import {
  listRoles,
  createRol,
  updateRol,
  deleteRol,
} from "../../services/rolesService";
import { ModalRol } from "../../modals/ModalRol";
import Modal from "antd/es/modal/Modal";
import { listCollaborators} from "../../services/collaboratorsService";  
import { listDeportistas} from "../../services/deportistasService"; 
import { listRepresentantes} from "../../services/representantesService"; 

/* ===== UI helpers ===== */
function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1",
        variants[variant]
      )}
    >
      {children}
    </span>
  );
}

function InboxIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

/* ===== View ===== */
export default function AdminRoles() {
  const { token } = useAuth() || {};

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState("");

  // búsqueda
  const [query, setQuery] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"

  // modal crear/editar
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // modal delete
  const [showDelete, setShowDelete] = useState(false);    
  const [deleting, setDeleting] = useState(false);
  const [targetDelete, setTargetDelete] = useState(null);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listRoles(token);
      setRoles(data || []);
      setError("");
    } catch (e) {
      console.error("GET roles failed", e);
      setError(e?.message || "Error al cargar roles");
      toast.error(e?.message || "Error al cargar roles");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const filtered = useMemo(() => {
    let result = roles;

    // Filtrar por activos
    if (onlyActive) {
      result = result.filter((r) => r.activo);
    }

    // Búsqueda por nombre o descripción
    const term = (query || "").toLowerCase().trim();
    if (term) {
      result = result.filter((r) => {
        const hay = `${r.nombre} ${r.descripcion || ""}`.toLowerCase();
        return hay.includes(term);
      });
    }

    return result;
  }, [roles, query, onlyActive]);

  function openCreate() {
    setEditing(null);
    setShowForm(true);
  }
  function openEdit(row) {
    setEditing(row);
    setShowForm(true);
  }
  function closeModal() {
    setShowForm(false);
    setEditing(null);
    setSaving(false);
  }

  async function handleSubmit(data) {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateRol(editing.id, data, token);
        toast.success("Rol actualizado");
      } else {
        await createRol(data, token);
        toast.success("Rol creado");
      }
      await loadRoles();
      closeModal();
    } catch (e) {
      console.error("Save role error", e);
      toast.error(e?.message || "No se pudo guardar el rol");
    } finally {
      setSaving(false);
    }
  }

async function confirmDelete() {
  if (!targetDelete?.id) return;

  setDeleting(true);
  try {
    // 1️⃣ Obtener todas las personas
    const [
      colaboradores,
      representantes,
      deportistas,
    ] = await Promise.all([
      listCollaborators(token),
      listRepresentantes(token),
      listDeportistas(token),
    ]);

    // 2️⃣ Unir todos en una sola lista
    const todos = [
      ...(colaboradores || []).map((u) => ({ ...u, tipo: "Colaborador" })),
      ...(representantes || []).map((u) => ({ ...u, tipo: "Representante" })),
      ...(deportistas || []).map((u) => ({ ...u, tipo: "Deportista" })),
    ];

    // 3️⃣ Filtrar por rol
    const usuariosConRol = todos.filter(
      (u) =>
        u.rol_id === targetDelete.id ||
        u.roles?.id === targetDelete.id
    );

    if (usuariosConRol.length > 0) {
      const nombres = usuariosConRol
        .slice(0, 5)
        .map((u) => `${u.nombres} ${u.apellidos} (${u.tipo})`)
        .join(", ");

      const mensaje =
        usuariosConRol.length <= 5
          ? `No se puede eliminar el rol porque está siendo utilizado por: ${nombres}`
          : `No se puede eliminar el rol porque ${usuariosConRol.length} usuarios lo están utilizando: ${nombres} y otros...`;

      toast.error(mensaje, { autoClose: 7000 });
      setDeleting(false);
      return;
    }

    // 4️⃣ Eliminar rol
    await deleteRol(targetDelete.id, token);

    toast.success("Rol eliminado");
    await loadRoles();

    setShowDelete(false);
    setTargetDelete(null);
  } catch (e) {
    console.error("Delete role error", e);
    toast.error("Error al verificar o eliminar el rol");
  } finally {
    setDeleting(false);
  }
}


  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">
            Roles
          </h1>
          <p className="mt-1 text-slate-600 text-sm">
            Gestión de roles de usuarios.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex-1">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            placeholder="Buscar rol por nombre o descripción…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        
        {/* Custom Checkbox for Active Filter */}
        <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
              className="peer sr-only"
            />
            <div className="w-5 h-5 rounded border-2 border-slate-300 bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all flex items-center justify-center">
              {onlyActive && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="font-medium">Mostrar solo activos</span>
        </label>
        
        {/* View Mode Switch */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode("cards")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "cards"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tarjetas"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "list"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de lista"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13" />
            </svg>
          </button>
        </div>
        
        <button
          onClick={loadRoles}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recargar datos"
        >
          <ReloadOutlined className="text-base" spin={loading} />
        </button>
        
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 active:scale-[.99] transition"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Nuevo rol
        </button>
      </div>

      {/* Errores */}
      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-56 rounded-2xl border border-slate-200 bg-white overflow-hidden"
            >
              <div className="animate-pulse h-full bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {/* Lista de roles */}
      {!loading && viewMode === "cards" && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <InboxIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">
                No se encontraron roles
              </h3>
              <p className="text-sm text-slate-500">
                {query ? "Intenta otra búsqueda" : "No hay roles registrados"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((r) => (
                <article
                  key={r.id}
                  className="flex flex-col overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-white hover:shadow-md transition"
                >
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold truncate">
                          {r.nombre}
                        </h3>
                        <div className="mt-1">
                          <Badge variant={r.activo ? "success" : "danger"}>
                            {r.activo ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {r.activo && (
                          <button
                            onClick={() => openEdit(r)}
                            className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                            title="Editar rol"
                            aria-label="Editar"
                          >
                            <svg
                              className="w-4 h-4 text-slate-700"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                        )}
                        {r.activo && (
                          <button
                            onClick={() => {
                              setTargetDelete(r);
                              setShowDelete(true);
                            }}
                            className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                            title="Eliminar rol"
                            aria-label="Eliminar"
                          >
                            <svg
                              className="w-4 h-4 text-rose-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Descripción */}
                    {r.descripcion && (
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {r.descripcion}
                      </p>
                    )}

                    {/* Permisos - SOLO READ */}
                    <div className="text-xs space-y-1 max-h-24 overflow-auto pr-1">
                      {Object.keys(r.permisos || {}).length === 0 ? (
                        <span className="text-slate-500">Sin permisos</span>
                      ) : (
                        Object.entries(r.permisos).map(([mod, val]) => {
                          // Extraer el valor de read
                          const hasRead = val?.read === true || val === true;

                          return (
                            <div
                              key={mod}
                              className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-2 py-1"
                            >
                              <span className="font-medium">{mod}</span>
                              <Badge variant={hasRead ? "success" : "muted"}>
                                {hasRead ? "✓ Lectura" : "✗ Sin acceso"}
                              </Badge>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Fecha */}
                    {r.creado_en && (
                      <p className="mt-3 text-[11px] text-slate-500">
                        Creado: {new Date(r.creado_en).toLocaleString()}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {/* List View (Table) */}
      {!loading && viewMode === "list" && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900">
                        {r.nombre}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-700 max-w-xs truncate">
                        {r.descripcion || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge
                        variant={r.activo ? "success" : "danger"}
                      >
                        {r.activo ? "Activo" : "Inactivo"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="inline-flex items-center justify-center rounded-lg border border-blue-600 text-blue-600 px-3 py-1.5 text-xs font-medium hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 transition"
                          title="Editar rol"
                        >
                          Editar
                        </button>
                        {r.activo && (
                          <button
                            onClick={() => {
                              setTargetDelete(r);
                              setShowDelete(true);
                            }}
                            className="inline-flex items-center justify-center rounded-lg border border-rose-600 text-rose-600 px-3 py-1.5 text-xs font-medium hover:bg-rose-50 hover:border-rose-400 transition"
                            title="Eliminar rol"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Empty State for List View */}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p className="text-sm">No se encontraron roles</p>
            </div>
          )}
        </div>
      )}

      {/* Modal Crear/Editar */}
      <ModalRol
        open={showForm}
        editing={editing}
        saving={saving}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      {/* Modal Eliminar */}
      <Modal
        open={showDelete}
        onCancel={() => {
          setShowDelete(false);
          setTargetDelete(null);
        }}
        title="Eliminar rol"
        footer={null}
        centered
      >
        <p className="text-sm text-slate-600 mb-4">
          ¿Seguro que deseas eliminar el rol{" "}
          <span className="font-medium text-slate-900">
            {targetDelete?.nombre}
          </span>
          ? Esta acción no se puede deshacer.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setShowDelete(false);
              setTargetDelete(null);
            }}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleting}
            className="rounded-xl bg-rose-600 text-white px-4 py-2 text-sm hover:bg-rose-700 disabled:opacity-60 transition"
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
